import { SITE } from './site';

const E = SITE.email;

export const cookiesDoc = {
  title: 'Cookie Policy',
  description:
    'Which cookies and similar technologies DashLink Gaming uses, why, and how to accept, decline or change your choice at any time.',
  intro:
    'Cookies are small text files stored by your browser. Similar technologies include local storage. This policy explains which ones DashLink Gaming uses and how you can control them. You can change your choice at any time using “Cookie settings” in the site footer.',
  sections: [
    {
      id: 'necessary',
      heading: '1. Strictly necessary (always on)',
      body: [
        'These are required for the Service to work and cannot be switched off. They do not track you across other websites.',
        {
          list: [
            'dashlink_token — an httpOnly, secure cookie that keeps you signed in. Scripts on the page cannot read it. Deleted when you log out and expires after 7 days.',
            'dashlink_cookie_consent (local storage) — remembers your cookie choice so we don’t ask on every visit. Kept for up to 12 months.',
            'user (session storage) — caches your public profile (username, avatar, score) for the current browser tab so the dashboard loads faster. Cleared when you log out or close the tab.',
          ],
        },
      ],
    },
    {
      id: 'analytics',
      heading: '2. Analytics (optional)',
      body: [
        'With your consent we use PostHog to understand which features are used and where people get stuck, so we can improve the Service. PostHog may set cookies or local storage entries beginning with “ph_”. If you decline analytics, we ask PostHog to stop capturing your activity.',
      ],
    },
    {
      id: 'advertising',
      heading: '3. Advertising (optional)',
      body: [
        'With your consent, Google AdSense and its partners may use cookies (such as __gads, __gpi, IDE and NID) to show ads, limit how often you see them, measure their performance and — where permitted — personalise them based on your visits to this and other websites. If you decline, we tell Google (via Google Consent Mode) not to use advertising cookies, and any ads shown will be non-personalised.',
        'You can also manage Google ad personalisation at https://adssettings.google.com and read how Google uses data at https://policies.google.com/technologies/partner-sites.',
      ],
    },
    {
      id: 'embeds',
      heading: '4. Third-party embeds',
      body: [
        'When a watch-party stream is opened in a room, the player is loaded from Twitch or YouTube. These services may set their own cookies under their own policies. You can avoid this by not opening embedded streams.',
      ],
    },
    {
      id: 'manage',
      heading: '5. Managing cookies',
      body: [
        'Use “Cookie settings” in the footer to accept or decline analytics and advertising cookies at any time. You can also block or delete cookies in your browser settings — note that blocking the strictly necessary sign-in cookie will stop you from logging in. We honour the Global Privacy Control (GPC) signal as a decline of advertising cookies.',
      ],
    },
    {
      id: 'contact',
      heading: '6. Questions',
      body: [`Email ${E}. See also our Privacy Policy at /privacy.`],
    },
  ],
};

export const guidelinesDoc = {
  title: 'Community Guidelines',
  description:
    'The DashLink Gaming community rules for chat, voice dictation, squads, watch parties and fair play — and what happens if they are broken.',
  intro:
    'DashLink is built for people who love games and want to play, chat and team up without the toxicity. These guidelines apply everywhere on the Service: game chats, room chats, usernames, profiles, squad invites and watch-party links.',
  sections: [
    {
      id: 'respect',
      heading: '1. Respect every player',
      body: [
        'Trash talk about a move is fine; attacking a person is not. No harassment, bullying, threats, stalking or targeted insults. No hate speech or slurs based on race, ethnicity, nationality, religion, caste, gender, gender identity, sexual orientation, disability or age.',
      ],
    },
    {
      id: 'fair-play',
      heading: '2. Play fair',
      body: [
        'Do not cheat, use bots, abuse bugs, collude to manipulate the leaderboard, or create multiple accounts to boost your score. Every move in our built-in games is validated by our server — if you find a bug, report it to us instead of exploiting it.',
      ],
    },
    {
      id: 'safe',
      heading: '3. Keep it safe',
      body: [
        {
          list: [
            'Never share passwords, and never post your own or anyone else’s address, phone number, school or financial details.',
            'No sexual content, gore, or content that sexualises minors — this is reported to authorities.',
            'No promotion of self-harm, violence, terrorism, illegal drugs or weapons.',
            'If someone pressures you to move to a private platform, share photos or send money, stop and report it.',
          ],
        },
      ],
    },
    {
      id: 'spam',
      heading: '4. No spam, scams or unwanted ads',
      body: [
        'Do not flood chats, mass-send squad invites, post referral links, sell accounts or in-game items, or share phishing links or “free currency” scams.',
      ],
    },
    {
      id: 'watch-parties',
      heading: '5. Watch parties',
      body: [
        'Only share streams from official Twitch or YouTube channels that you are allowed to share. No pirated broadcasts, and no streams with content that would break these guidelines. Remember that everyone in the room sees what you embed.',
      ],
    },
    {
      id: 'voice',
      heading: '6. Voice dictation',
      body: [
        'Voice dictation turns your speech into text before you send it. Always read the transcript before pressing send — you are responsible for the message exactly as it is posted.',
      ],
    },
    {
      id: 'names',
      heading: '7. Usernames and profiles',
      body: [
        'Usernames, avatars and bios must follow these rules too. Do not impersonate other players, streamers, staff or brands.',
      ],
    },
    {
      id: 'reporting',
      heading: '8. Reporting a problem',
      body: [
        `Email ${E} with the username, the game or room, the approximate time, and a screenshot if you can. We review every report and keep the reporter’s identity confidential.`,
      ],
    },
    {
      id: 'enforcement',
      heading: '9. What happens if rules are broken',
      body: [
        'Depending on how serious it is, we may remove content, issue a warning, temporarily restrict chat or rooms, reset scores, or permanently close the account. Illegal content may be reported to the authorities. If you think we got it wrong, email us and we will take another look.',
      ],
    },
  ],
};
